/**
 * Generic optimistic mutation helper.
 *
 * Encapsulates the common pattern:
 * 1. Save previous state (for potential rollback)
 * 2. Apply optimistic update + sync to stores/caches
 * 3. Make API call
 * 4. On success: optionally transform state with server response + sync
 * 5. On failure: rollback to previous state + sync
 *
 * This eliminates the repeated try/catch/rollback boilerplate found in
 * taskApi.ts (8 functions) and stores/favorites.ts (4 functions).
 *
 * @see lib/taskApi.ts — task mutations using this helper
 * @see lib/stores/favorites.ts — favorite view mutations using this helper
 */

export interface OptimisticMutationConfig<TState, TApiResult> {
  /** Get current state (saved for rollback on failure) */
  getState: () => TState;
  /** Pure transform: apply the optimistic update */
  applyOptimistic: (state: TState) => TState;
  /** Sync state to stores/caches (called on optimistic update, success, and rollback) */
  syncState: (state: TState) => void;
  /** The API call to perform */
  apiCall: () => Promise<TApiResult>;
  /** Transform state on success using API result (optional — uses optimistic state if omitted) */
  onSuccess?: (optimisticState: TState, apiResult: TApiResult) => TState;
}

export interface OptimisticMutationResult<TState> {
  success: boolean;
  state: TState;
  error?: string;
  /** Original error object (useful for rethrowing) */
  cause?: unknown;
}

/**
 * Execute an optimistic mutation with automatic rollback on failure.
 *
 * @example
 * // Basic usage (no success transform — optimistic state is final)
 * const result = await optimisticMutation({
 *   getState: () => [...items],
 *   applyOptimistic: (items) => items.filter(i => i.id !== targetId),
 *   syncState: (items) => cache.set(items),
 *   apiCall: () => fetch('/api/items/' + targetId, { method: 'DELETE' }),
 * });
 *
 * @example
 * // With success transform (merge server response into state)
 * const result = await optimisticMutation({
 *   getState: () => [...items],
 *   applyOptimistic: (items) => items.map(i => i.id === id ? { ...i, title } : i),
 *   syncState: (items) => cache.set(items),
 *   apiCall: () => api.updateItem(id, { title }),
 *   onSuccess: (items, serverItem) => items.map(i => i.id === id ? serverItem : i),
 * });
 */
export async function optimisticMutation<TState, TApiResult = void>(
  config: OptimisticMutationConfig<TState, TApiResult>,
): Promise<OptimisticMutationResult<TState>> {
  const prevState = config.getState();
  const optimisticState = config.applyOptimistic(prevState);
  config.syncState(optimisticState);

  try {
    const apiResult = await config.apiCall();

    if (config.onSuccess) {
      const finalState = config.onSuccess(optimisticState, apiResult);
      config.syncState(finalState);
      return { success: true, state: finalState };
    }

    return { success: true, state: optimisticState };
  } catch (e) {
    config.syncState(prevState);
    return {
      success: false,
      state: prevState,
      error: e instanceof Error ? e.message : String(e),
      cause: e,
    };
  }
}
